import {
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  Group,
  List,
  rem,
  Stack,
  Text,
  Title,
} from "@mantine/core"
import { IconArrowRight, IconChecklist } from "@tabler/icons-react"
import { createFileRoute } from "@tanstack/react-router"

import { Navbar } from "@/components/Navbar"

export const Route = createFileRoute("/")({
  component: LandingPage,
})

function getAgentTodoUrl(): string {
  const appUrl = (import.meta.env["VITE_APP_URL"] as string) || ""
  if (appUrl.includes("dev.")) {
    return "https://dev.agent-todo.com"
  }
  return "https://agent-todo.com"
}

function LandingPage() {
  const products = [
    {
      name: "Agent Todo",
      description: "Smart task management across Microsoft Todo and Google Tasks. AI-powered prioritization and sync.",
      icon: IconChecklist,
      url: getAgentTodoUrl(),
      gradient: { from: "#12b886", to: "#20c997" },
    },
  ]

  return (
    <Box style={{ minHeight: "100vh", position: "relative" }}>
      <Navbar transparent />

      <Container size="md" style={{ paddingTop: "120px", paddingBottom: "80px" }}>
        <Stack gap="xl">
          {/* Hero */}
          <Stack align="center" gap="md">
            <Badge size="lg" variant="outline" radius="xl">
              AI Agent Platform
            </Badge>

            <Title
              style={{
                fontSize: rem(56),
                fontWeight: 900,
                lineHeight: 1.1,
                textAlign: "center",
                maxWidth: "800px",
              }}
            >
              <Text component="span" variant="gradient" gradient={{ from: "#667eea", to: "#764ba2", deg: 45 }} inherit>
                Agent Gate
              </Text>{" "}
              — Your AI Agent Hub
            </Title>

            <Text size="xl" c="dimmed" ta="center" maw={640}>
              Agent Gate connects the everyday tools you already use — like Microsoft Todo and Google Tasks — to AI
              assistants such as Claude and ChatGPT, so AI can read and manage your tasks on your behalf.
            </Text>
          </Stack>

          {/* What it is */}
          <Stack gap="sm" mt="xl">
            <Title order={2}>What Agent Gate does</Title>
            <Text c="dimmed">
              Agent Gate is a central hub for a growing suite of AI agent applications. Each agent on the hub connects a
              service you already use to AI assistants through the Model Context Protocol (MCP) — an open standard that
              lets assistants like Claude Desktop, Claude Code, and ChatGPT read and act on your data when you ask them
              to. Instead of copy-pasting between apps and your AI chat window, Agent Gate lets the assistant work
              directly with your tools. Agent Todo is the first agent on the hub, with more coming.
            </Text>
          </Stack>

          {/* Products */}
          <Stack gap="md" mt="xl">
            <Title order={2}>Products</Title>
            <Grid gap="lg">
              {products.map((product) => (
                <Grid.Col span={{ base: 12, sm: 6 }} key={product.name}>
                  <Card shadow="sm" padding="lg" radius="md" withBorder style={{ height: "100%" }}>
                    <Group mb="md">
                      <Box
                        style={{
                          background: `linear-gradient(135deg, ${product.gradient.from}, ${product.gradient.to})`,
                          borderRadius: "8px",
                          padding: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <product.icon size={24} color="white" />
                      </Box>
                      <Title order={4}>{product.name}</Title>
                    </Group>

                    <Text size="sm" c="dimmed" mb="md">
                      {product.description}
                    </Text>

                    <Button
                      component="a"
                      href={product.url}
                      variant="gradient"
                      gradient={product.gradient}
                      rightSection={<IconArrowRight size={16} />}
                      fullWidth
                    >
                      Get Started
                    </Button>
                  </Card>
                </Grid.Col>
              ))}
            </Grid>
            <Text size="sm" c="dimmed">
              More agents coming soon.
            </Text>
          </Stack>

          {/* How your data is used */}
          <Stack gap="sm" mt="xl">
            <Title order={2}>How Agent Gate uses your Google Tasks data (via its Agent Todo product)</Title>
            <Text c="dimmed">
              Agent Todo is the Agent Gate product that connects to Google Tasks. When you connect your Google account,
              Agent Gate — through Agent Todo — requests the{" "}
              <Text component="span" ff="monospace" fz="sm">
                https://www.googleapis.com/auth/tasks
              </Text>{" "}
              scope. On your behalf, this lets Agent Gate:
            </Text>
            <List c="dimmed" spacing="xs">
              <List.Item>
                Read your Google Tasks lists so you can view them in the dashboard and query them through connected AI
                assistants.
              </List.Item>
              <List.Item>
                Create, update, and complete tasks when you request it — in the dashboard or through an AI assistant.
              </List.Item>
              <List.Item>
                Sync tasks between Google Tasks and Microsoft Todo, if you connect both, into one list.
              </List.Item>
            </List>
            <Text c="dimmed">
              Agent Todo only reads and writes your task data — it does not access any other Google service. Task data
              is stored encrypted and is never sold or used for advertising. The same read/create/update/complete
              functionality is available for Microsoft Todo via Microsoft Graph.
            </Text>
          </Stack>

          {/* Your control */}
          <Stack gap="sm" mt="xl">
            <Title order={2}>Your data, your control</Title>
            <List c="dimmed" spacing="xs">
              <List.Item>
                Disconnect any provider at any time from Settings in the app — this immediately revokes access and
                deletes the stored OAuth tokens and cached task data for that provider.
              </List.Item>
              <List.Item>
                You can also revoke access directly from your Google Account at{" "}
                <Anchor href="https://myaccount.google.com/permissions">myaccount.google.com/permissions</Anchor>.
              </List.Item>
            </List>
            <Text c="dimmed" fz="sm">
              Agent Gate's use and transfer of information received from Google APIs adheres to the{" "}
              <Anchor href="https://developers.google.com/terms/api-services-user-data-policy">
                Google API Services User Data Policy
              </Anchor>
              , including the Limited Use requirements.
            </Text>
          </Stack>

          <Divider mt="xl" />

          {/* Footer */}
          <Stack gap="xs">
            <Text size="sm" c="dimmed">
              Agent Gate is an independent product and is not affiliated with, endorsed by, or sponsored by Google or
              Microsoft.
            </Text>
            <Group gap="lg">
              <Anchor href="/privacy" size="sm">
                Privacy Policy
              </Anchor>
              <Anchor href="/terms" size="sm">
                Terms of Service
              </Anchor>
              <Anchor href="mailto:jordan.burke@gmail.com" size="sm">
                Contact
              </Anchor>
            </Group>
          </Stack>
        </Stack>
      </Container>
    </Box>
  )
}
